import { useState } from "react"

import {
  AboutGuestsForm,
  type AboutGuestsTarget,
} from "./AboutGuestsForm"
import { AboutGuestsEditForm } from "./AboutGuestsEditForm"
import { AboutGuestsList } from "./AboutGuestsList"
import {
  guestProfileCategories,
  playbookKeyByGuestProfileField,
  type GuestProfileCategory,
} from "./aboutGuestsOptions"
import { PlaybookGuidance } from "./PlaybookGuidance"
import type {
  StayGuestProfileData,
  StayGuestProfileField,
  StayGuestProfileTraveller,
  StayPlaybookTemplates,
} from "./stayData"
import { VacationOverviewCard } from "./VacationOverviewCard"

type AboutGuestsSectionProps = {
  authenticityToken: string
  guestProfile: StayGuestProfileData
  playbookTemplates: StayPlaybookTemplates
  selectedTravellerId?: string
}

type GuestProfileTravellerUpdate = {
  field: StayGuestProfileField
  traveller: StayGuestProfileTraveller
  values: readonly string[]
}

type AboutGuestsMode = "add" | "edit"

export function AboutGuestsSection({
  authenticityToken,
  guestProfile,
  playbookTemplates,
  selectedTravellerId,
}: AboutGuestsSectionProps) {
  const [travellers, setTravellers] = useState(guestProfile.travellers)
  const [activeField, setActiveField] = useState<StayGuestProfileField | null>(null)
  const [activeMode, setActiveMode] = useState<AboutGuestsMode | null>(null)
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null)
  const [selectedValue, setSelectedValue] = useState("")
  const [editValues, setEditValues] = useState<readonly string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const profileTraveller = selectedTravellerId
    ? travellers.find((traveller) => traveller.id === selectedTravellerId)
    : null
  const visibleTravellers = profileTraveller ? [profileTraveller] : travellers
  const selectedCategory = activeField
    ? guestProfileCategories.find((category) => category.field === activeField) ?? guestProfileCategories[0]!
    : guestProfileCategories[0]!
  const selectedTraveller =
    selectedTargetId ? travellers.find((traveller) => traveller.id === selectedTargetId) : null
  const selectedTargetTravellers =
    selectedTargetId === null ? [] : selectedTargetId ? (selectedTraveller ? [selectedTraveller] : []) : visibleTravellers
  const targets: readonly AboutGuestsTarget[] = profileTraveller
    ? [{ id: profileTraveller.id, label: profileTraveller.display_name }]
    : [
      { id: "", label: "Whole party" },
      ...travellers.map((traveller) => ({
        id: traveller.id,
        label: traveller.display_name,
      })),
    ]

  if (!visibleTravellers.length) {
    return null
  }

  const selectedDisplayValues = selectedTargetTravellers.flatMap((traveller) =>
    traveller[selectedCategory.field].filter((value) => value.trim()),
  )
  const remainingOptions = selectedCategory.options.filter((option) => {
    if (option === "None") return selectedDisplayValues.length > 0

    return selectedTargetTravellers.some((traveller) =>
      !traveller[selectedCategory.field].filter((value) => value.trim()).includes(option),
    )
  })
  const canAddValue = Boolean(
    activeMode === "add" &&
      activeField &&
      selectedValue &&
      remainingOptions.includes(selectedValue) &&
      selectedTargetTravellers.length &&
      !isSaving,
  )
  const canSaveEdit = Boolean(
    activeMode === "edit" &&
      activeField &&
      selectedTraveller &&
      !valuesEqual(editValues, selectedTraveller[activeField].filter((value) => value.trim())) &&
      !isSaving,
  )

  function openAddForm(field: StayGuestProfileField) {
    setActiveField(field)
    setActiveMode("add")
    setSelectedTargetId(profileTraveller?.id ?? null)
    setSelectedValue("")
    setEditValues([])
    setError(null)
  }

  function openEditForm(category: GuestProfileCategory, traveller: StayGuestProfileTraveller) {
    setActiveField(category.field)
    setActiveMode("edit")
    setSelectedTargetId(traveller.id)
    setSelectedValue("")
    setEditValues(traveller[category.field].filter((value) => value.trim()))
    setError(null)
  }

  function closeField() {
    if (isSaving) return

    setActiveField(null)
    setActiveMode(null)
    setSelectedTargetId(null)
    setSelectedValue("")
    setEditValues([])
    setError(null)
  }

  async function addValue() {
    if (!canAddValue || !activeField) return

    const updates = selectedTargetTravellers.map((traveller) => {
      const currentValues = traveller[activeField].filter((value) => value.trim())
      const values = selectedValue === "None"
        ? [""]
        : currentValues.includes(selectedValue)
          ? currentValues
          : [...currentValues, selectedValue]

      return { field: activeField, traveller, values }
    })

    const didUpdate = await updateTravellers(updates)

    if (didUpdate) {
      resetField()
    }
  }

  async function saveEditValues() {
    if (!canSaveEdit || !activeField || !selectedTraveller) return

    const didUpdate = await updateTravellers([
      { field: activeField, traveller: selectedTraveller, values: editValues },
    ])

    if (didUpdate) {
      resetField()
    }
  }

  async function updateTravellers(updates: readonly GuestProfileTravellerUpdate[]) {
    setError(null)
    setIsSaving(true)

    try {
      const updatedTravellers = await Promise.all(
        updates.map(({ field, traveller, values }) =>
          patchTraveller(traveller.id, field, values, authenticityToken),
        ),
      )
      const updatedTravellerById = new Map(updatedTravellers.map((traveller) => [traveller.id, traveller]))

      setTravellers((currentTravellers) =>
        currentTravellers.map((currentTraveller) =>
          updatedTravellerById.get(currentTraveller.id) ?? currentTraveller,
        ),
      )
      return true
    } catch {
      setError("Guest profile could not be updated.")
      return false
    } finally {
      setIsSaving(false)
    }
  }

  function resetField() {
    setActiveField(null)
    setActiveMode(null)
    setSelectedTargetId(null)
    setSelectedValue("")
    setEditValues([])
    setError(null)
  }

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <h2 className="text-base font-semibold uppercase leading-5 tracking-[0.96px] text-[#525252]">
        Dietary & Special Requirements
      </h2>

      <div className="flex flex-col gap-6">
        {guestProfileCategories.map((category) => {
          const isOpen = activeField === category.field
          const playbookKey = playbookKeyByGuestProfileField[category.field]
          const playbookCopy = playbookTemplates[playbookKey]
          const hasValues = visibleTravellers.some((traveller) =>
            traveller[category.field].some((value) => value.trim()),
          )

          return (
            <VacationOverviewCard
              actionExpanded={isOpen}
              actionHasPopup="dialog"
              actionLabel={`Add ${category.label}`}
              key={category.field}
              onAction={() => openAddForm(category.field)}
              title={category.label}
            >
              <PlaybookGuidance copy={playbookCopy} topic={category.label} />

              <AboutGuestsList
                category={category}
                isSaving={isSaving}
                onEditValues={openEditForm}
                travellers={visibleTravellers}
              />

              {!hasValues && (profileTraveller || !playbookCopy) && !isOpen ? (
                <p className="rounded-[4px] border border-dashed border-[#cfcfcf] bg-[#fafafa] px-3 py-2 text-sm font-medium leading-5 text-[#817d76]">
                  No {category.label.toLowerCase()} recorded yet.
                </p>
              ) : null}
            </VacationOverviewCard>
          )
        })}
      </div>

      {activeMode === "add" && activeField ? (
        <AboutGuestsForm
          canAddValue={canAddValue}
          category={selectedCategory}
          error={error}
          isSaving={isSaving}
          onAddValue={() => void addValue()}
          onClose={closeField}
          onTargetChange={(targetId) => {
            setSelectedTargetId(targetId)
            setSelectedValue("")
          }}
          onValueChange={setSelectedValue}
          remainingOptions={remainingOptions}
          selectedTargetId={selectedTargetId}
          selectedValue={selectedValue}
          targets={targets}
        />
      ) : null}

      {activeMode === "edit" && activeField && selectedTraveller ? (
        <AboutGuestsEditForm
          canSave={canSaveEdit}
          category={selectedCategory}
          error={error}
          isSaving={isSaving}
          onClose={closeField}
          onRemoveValue={(value) =>
            setEditValues((currentValues) => currentValues.filter((currentValue) => currentValue !== value))
          }
          onSave={() => void saveEditValues()}
          traveller={selectedTraveller}
          values={editValues}
        />
      ) : null}
    </section>
  )
}

function valuesEqual(first: readonly string[], second: readonly string[]) {
  return first.length === second.length && first.every((value, index) => value === second[index])
}

async function patchTraveller(
  travellerId: string,
  field: StayGuestProfileField,
  values: readonly string[],
  authenticityToken: string,
) {
  const response = await fetch(`/api/travellers/${travellerId}`, {
    body: JSON.stringify({ traveller: { [field]: values } }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method: "PATCH",
  })

  if (!response.ok) {
    throw new Error(`Traveller update failed with ${response.status}`)
  }

  return (await response.json()) as StayGuestProfileTraveller
}
